import React, { useEffect, useState } from 'react';
import { seChoicesService } from '../../_services/seChoices.service';
import { choiceChip, CHOICE_COLUMNS } from '../staff/seChoices/choiceChips';
import { DISCOUNTS_VISIBLE } from '../../config/featureFlags';
import { loadChoices } from '../discounts/discountStore';
import './DiarySharePanel.css';

/**
 * Team › Sharing (owner or a `team` role): what each self-employed stylist
 * chose, read-only, in the same rows as the other Sharing tabs. `keys` picks
 * the choices (choiceChips keys: ['bookingPage'] on Bookings; marketing,
 * loyalty and codes on Marketing). Only the stylist can change them.
 */

const CheckIcon = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M2.5 7.5L5.5 10.5L11.5 3.5" /></svg>
);
const CrossIcon = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><path d="M3.5 3.5L10.5 10.5M10.5 3.5L3.5 10.5" /></svg>
);

const initials = (name) => String(name || '').trim().split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('') || '?';
const firstName = (name) => String(name || '').trim().split(/\s+/)[0] || 'them';

export default function SeChoicesList({ keys, title = 'Self-employed stylists', lede }) {
    const [rows, setRows] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        let off = false;
        seChoicesService.team()
            // Discount codes: the server's answer when it sends one, else the device stand-in (staging)
            .then((d) => (DISCOUNTS_VISIBLE && keys.includes('codes') ? loadChoices().then((codes) => ({
                ...d,
                stylists: (d.stylists || []).map((st) => ({ ...st, choices: { ...(st.choices || {}), codes: (st.choices && st.choices.codes) || codes[String(st.userId)] || codes[String(st.hairdresserId)] || null } })),
            })).catch(() => d) : d))
            .then((d) => { if (!off) setRows(d.stylists || []); })
            .catch(() => { if (!off) setError(true); });
        return () => { off = true; };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // The owner is reading, so the salon's codes are theirs.
    const LABELS = { codes: 'Your discount codes' };
    const columns = CHOICE_COLUMNS.filter((c) => keys.includes(c.key)).map((c) => ({ ...c, label: LABELS[c.key] || c.label }));

    return (
        <>
            <p className="beu-section-label" style={{ margin: '24px 0 6px' }}>{title}</p>
            {lede && <p className="dsp-manager-note" style={{ margin: '0 0 14px' }}>{lede}</p>}
            {error && <p className="dsp-manager-note">We couldn&rsquo;t load their choices just now. Try again in a moment.</p>}
            {!error && !rows && <div className="dsp-loading">Loading...</div>}
            {rows && rows.length === 0 && <p className="dsp-manager-note">No self-employed stylists here yet.</p>}
            {rows && rows.length > 0 && (
                <div className="settings-card" style={{ padding: '4px 24px' }}>
                    {rows.map((s) => (
                        <div key={s.hairdresserId} className="dsp-staff-row">
                            <div className="dsp-staff-header">
                                <div className="dsp-staff-avatar">{initials(s.name)}</div>
                                <div className="dsp-staff-meta">
                                    <span className="dsp-staff-name">{s.name}</span>
                                    <span className="dsp-staff-role">SELF-EMPLOYED</span>
                                </div>
                            </div>
                            <div className="dsp-staff-controls">
                                <div className="dsp-readonly-row">
                                    <span className="dsp-readonly-note">Managed by {firstName(s.name)}</span>
                                </div>
                                {columns.map((col) => {
                                    const c = choiceChip(col.key, s.choices);
                                    const on = c.tone === 'blue' || c.tone === 'ok';
                                    return (
                                        <div key={col.key} className={`dsp-reciprocity ${on ? 'dsp-reciprocity--yes' : 'dsp-reciprocity--no'}`} title={c.hint}>
                                            {on ? <CheckIcon /> : <CrossIcon />}
                                            <span>{col.key === 'bookingPage' ? (on ? 'On your booking page' : 'Hidden from your booking page') : `${col.label}: ${c.label}`}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
}
